import React, { useState } from 'react'

import Modal from '../Modal'
import DefaultButton from '../Button/Button'
// import { ResidentDTO } from '../../interfaces/resident'
import Input from '../Input'
import CustomizedDropdown from '../Dropdown/CustomizedDropdown'
import ResidentService from '../../api/resident-service'
import { ResidentDTO } from '../../interfaces/resident'

type ResidentAccessDuration =
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'HALF_YEARLY'
  | 'ANNUALLY'

  type ResidentAccessPayload = {
  enabledUntil?: string
  enabledAt?: string
  amount?: string
  reason?: string
}

interface ResidentAccessModalProps {
  open: boolean
  close: () => void
  resident?: ResidentDTO | null
  address?: string
  propertyMemberId?: string
  onAccessUpdated: () => Promise<unknown>
  handleViewSelect: (
    view: string,
    queries?: Record<string, string>
  ) => void
}

const accessOptions = [
  {
    label: 'Enable until',
    value: 'enable_until',
    labelClassName: 'text-[#6C757D]',
    selectedClassName: 'bg-[#E9ECEF]',
  },
  {
    label: 'Enable indefinite',
    value: 'enable_indefinite',
    labelClassName: 'text-[#6C757D]',
    selectedClassName: 'bg-[#E9ECEF]',
  },
  {
    label: 'Disable',
    value: 'disable',
    labelClassName: 'text-[#DC3545]',
    selectedClassName: 'bg-[#FDECEA]',
  },
]

const durationOptions = [
  {
    label: 'Monthly',
    value: 'MONTHLY',
    labelClassName: 'text-[#6C757D]',
    selectedClassName: 'bg-[#E9ECEF]',
  },
  {
    label: 'Quarterly',
    value: 'QUARTERLY',
    labelClassName: 'text-[#6C757D]',
    selectedClassName: 'bg-[#E9ECEF]',
  },
  {
    label: 'Half Yearly',
    value: 'HALF_YEARLY',
    labelClassName: 'text-[#6C757D]',
    selectedClassName: 'bg-[#E9ECEF]',
  },
  {
    label: 'Annually',
    value: 'ANNUALLY',
    labelClassName: 'text-[#6C757D]',
    selectedClassName: 'bg-[#E9ECEF]',
  },
]

const disableReasonsList = [
  'Outstanding levy',
  'Partial payment received',
  'Violation of estate rules',
  'Resident moved out',
  'Other',
]

const ResidentAccessModal: React.FC<ResidentAccessModalProps> = ({
  open,
  close,
  resident,
    address,
  propertyMemberId,
  onAccessUpdated,
}) => {

  const [accessStatus, setAccessStatus] = useState('')
  const [reasons, setReasons] = useState<string[]>([])
  const [otherReason, setOtherReason] = useState('')

  const [startDate, setStartDate] = useState('')
  const [duration, setDuration] =
    useState<ResidentAccessDuration | ''>('')
  const [amount, setAmount] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { updateResidentAccess } =
    ResidentService.useUpdateResidentAccess()


  const isOtherSelected = reasons.includes('Other')
  const isDisableAction = accessStatus === 'disable'

  const handleAccessChange = (value: string) => {
    setAccessStatus(value)

    setReasons([])
    setOtherReason('')
    setStartDate('')
    setDuration('')
    setAmount('')
  }

  const toggleReason = (reason: string) => {
    setReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    )
  }

  const calculateEnableUntil = (
  startDate: string,
  duration: ResidentAccessDuration
) => {
  const date = new Date(`${startDate}T00:00:00`)

  switch (duration) {
    case 'MONTHLY':
      date.setMonth(date.getMonth() + 1)
      break

    case 'QUARTERLY':
      date.setMonth(date.getMonth() + 3)
      break

    case 'HALF_YEARLY':
      date.setMonth(date.getMonth() + 6)
      break

    case 'ANNUALLY':
      date.setFullYear(date.getFullYear() + 1)
      break
  }

  return date.toISOString()
}

  const getPrimaryActionLabel = () => {
    switch (accessStatus) {
      case 'disable':
        return 'Disable'

      case 'enable_until':
      case 'enable_indefinite':
        return 'Save'

      default:
        return null
    }
  }

  const primaryLabel = getPrimaryActionLabel()

  const handleSubmit = async () => {

  if (!propertyMemberId) {
    console.error(
      'Cannot update resident access: propertyMemberAccess._id is missing',
      resident
    )
    return
  }

    let action: 'enabled' | 'disabled' | 'indefinite'

  if (accessStatus === 'disable') {
  action = 'disabled'
} else if (accessStatus === 'enable_until') {
  action = 'enabled'
} else if (accessStatus === 'enable_indefinite') {
  action = 'indefinite'
} else {
  console.error('No access action selected')
  return
}

    try {
      setIsSubmitting(true)

      let payload: ResidentAccessPayload = {}

      if (accessStatus === 'enable_until') {
        if (!startDate) {
          console.error('Payment start date is required')
          return
        }

        if (!duration) {
          console.error('Duration is required')
          return
        }

        if (!amount) {
          console.error('Amount is required')
          return
        }

        const enabledAt = new Date(
          `${startDate}T00:00:00`
        )

        const enabledUntil = calculateEnableUntil(
          startDate,
          duration
        )

        payload = {
          enabledAt: enabledAt.toISOString(),
          enabledUntil,
          amount,
        }
      }

      if (accessStatus === 'enable_indefinite') {
        if (!startDate) {
          console.error('Payment start date is required')
          return
        }

        if (!amount) {
          console.error('Amount is required')
          return
        }

        payload = {
          enabledAt: new Date(
            `${startDate}T00:00:00`
          ).toISOString(),
          amount,
        }
      }

      if (accessStatus === 'disable') {
        if (reasons.length === 0) {
          console.error(
            'Please select at least one reason'
          )
          return
        }

        if (
          isOtherSelected &&
          !otherReason.trim()
        ) {
          console.error(
            'Please enter the other reason'
          )
          return
        }

        const reason = isOtherSelected
          ? otherReason.trim()
          : reasons.join(', ')

        payload = {
          reason,
        }
      }

      const response =
        await updateResidentAccess(
          propertyMemberId,
          action,
          payload
        )

      await onAccessUpdated()
      close()
    } catch (error) {
      console.error(
        '=== RESIDENT ACCESS API ERROR ===',
        error
      )
    } finally {
      setIsSubmitting(false)
    }
  }


  return (
    <Modal
      open={open}
      close={close}
      widthClass="w-[calc(100%-16px)] sm:w-[90vw] md:w-full md:max-w-[700px]"
      modalClassName="md:min-w-[673px] w-[352px] md:min-h-[330px] mt-2 p-4 sm:p-4"
    >
      {/* HEADER */}
      <div className="mt-4 flex flex-col gap-3">
        <p className="font-semibold text-xl text-[#212529]">
          Access Control
        </p>

        <p className="text-[#495057] font-medium">
          {address || 'N/A'}
        </p>
      </div>

      {/* ACCESS STATUS */}
      <div className="mt-6 space-y-3">
        <label className="font-medium text-[#6C757D]">
          Access Status
        </label>

        <CustomizedDropdown
          options={accessOptions}
          value={accessStatus}
          onSelect={handleAccessChange}
          defaultLabel="Please select"
        />
      </div>

      {/* ENABLE UNTIL */}
      {accessStatus === 'enable_until' && (
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <label className="text-[#6C757D] font-medium">
              Payment Start Date
            </label>

            <Input
              type="date"
              name="startDate"
              value={startDate}
              onChange={(e) =>
                setStartDate(e.target.value)
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-[#6C757D] font-medium">
              Duration
            </label>

            <CustomizedDropdown
              options={durationOptions}
              value={duration}
              onSelect={(value) =>
                setDuration(
                  value as ResidentAccessDuration
                )
              }
              defaultLabel="Select duration"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[#6C757D] font-medium">
              Amount
            </label>

            <Input
              type="number"
              placeholder="N 8000"
              name="amount"
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value)
              }
            />
          </div>
        </div>
      )}

      {/* ENABLE INDEFINITE */}
      {accessStatus === 'enable_indefinite' && (
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <label className="text-[#6C757D] font-medium">
              Payment Start Date
            </label>

            <Input
              type="date"
              name="startDate"
              value={startDate}
              onChange={(e) =>
                setStartDate(e.target.value)
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-[#6C757D] font-medium">
              Amount
            </label>

            <Input
              type="number"
              placeholder="N 8000"
              name="amount"
              value={amount}
              onChange={(e) =>
                setAmount(e.target.value)
              }
            />
          </div>
        </div>
      )}

      {/* DISABLE */}
      {accessStatus === 'disable' && (
        <div className="mt-4 space-y-4">
          <p className="text-[#6C757D] font-medium">
            This will immediately revoke the resident&apos;s
            access for this property
          </p>

          <p className="font-medium text-sm">
            Please select reason(s) for disabling
          </p>

          <div className="space-y-2">
            {disableReasonsList.map((reason) => (
              <label
                key={reason}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={reasons.includes(reason)}
                  onChange={() =>
                    toggleReason(reason)
                  }
                />

                {reason}
              </label>
            ))}
          </div>

          {isOtherSelected && (
            <input
              type="text"
              value={otherReason}
              onChange={(e) =>
                setOtherReason(e.target.value)
              }
              placeholder="Enter reason"
              className="w-full p-2 border rounded"
            />
          )}
        </div>
      )}

      {/* ACTION BUTTONS */}
      <div className="mt-6 flex justify-end gap-2">
        <DefaultButton
          buttonType="secondary"
          onClick={close}
          size="sm"
          disabled={isSubmitting}
        >
          Close
        </DefaultButton>

        {primaryLabel && (
          <DefaultButton
            type="button"
            buttonType={
              isDisableAction ? 'danger' : 'primary'
            }
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? 'Processing...'
              : primaryLabel}
          </DefaultButton>
        )}
      </div>
    </Modal>
  )
}

export default ResidentAccessModal